import Icon from "@imminently/decisively-ui/Icon";
import * as React from "react"
import Button from "@imminently/decisively-ui/Button"
import Card from "@imminently/decisively-ui/Card"
import TextInput from "@imminently/decisively-ui/TextInput"
import { Label } from "@/ui/primitives/label"
import Select from "@imminently/decisively-ui/Select"
import Switch from "@imminently/decisively-ui/Switch"

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
} from "../components/ComponentPage"

export function CardPage() {
  return (
    <ComponentPage
      title="Card"
      description="Displays a card with header, content, and footer."
    >
      {/* Basic Card */}
      <ComponentSection title="Card sizes" description="Use small cards for compact status and action rows.">
        <ComponentPreview>
          <div className="flex flex-wrap gap-4">
            <Card className="w-[350px]">
              <Card.Header>
                <Card.Title>Default card</Card.Title>
                <Card.Description>Card description goes here.</Card.Description>
              </Card.Header>
              <Card.Content><p>Card content can contain any elements.</p></Card.Content>
              <Card.Footer><Button className="w-full" label="Action">Action</Button></Card.Footer>
            </Card>
            <Card size="small" className="w-[350px]">
              <Card.Header>
                <Card.Title>Small card</Card.Title>
                <Card.Description>Reduced padding and section spacing.</Card.Description>
              </Card.Header>
              <Card.Content><p>Use for compact information and actions.</p></Card.Content>
              <Card.Footer><Button className="w-full" label="Action">Action</Button></Card.Footer>
            </Card>
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Card with Form */}
      <ComponentSection title="Card with Form" description="Card containing a form">
        <ComponentPreview>
          <Card className="w-[350px]">
            <Card.Header>
              <Card.Title>Create project</Card.Title>
              <Card.Description>Deploy your new project in one-click.</Card.Description>
            </Card.Header>
            <Card.Content>
              <form>
                <div className="grid w-full items-center gap-4">
                  <div className="flex flex-col space-y-1.5">
                    <Label htmlFor="name">Name</Label>
                    <TextInput id="name" placeholder="Name of your project" />
                  </div>
                  <div className="flex flex-col space-y-1.5">
                    <Label htmlFor="framework">Framework</Label>
                    <Select
                      options={[
                        { value: "next", label: "Next.js" },
                        { value: "sveltekit", label: "SvelteKit" },
                        { value: "astro", label: "Astro" },
                        { value: "nuxt", label: "Nuxt.js" },
                      ]}
                      placeholder="Select"
                    />
                  </div>
                </div>
              </form>
            </Card.Content>
            <Card.Footer className="flex justify-between">
              <Button variant="outline" label="Cancel">Cancel</Button>
              <Button label="Deploy">Deploy</Button>
            </Card.Footer>
          </Card>
        </ComponentPreview>
      </ComponentSection>

      {/* Notification Card */}
      <ComponentSection title="Notifications Card" description="Card for notifications">
        <ComponentPreview>
          <Card className="w-[380px]">
            <Card.Header>
              <Card.Title>Notifications</Card.Title>
              <Card.Description>You have 3 unread messages.</Card.Description>
            </Card.Header>
            <Card.Content className="grid gap-4">
              <div className="flex items-center space-x-4 rounded-md border p-4">
                <Icon name="bell" className="h-5 w-5" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium leading-none">
                    Push Notifications
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Send notifications to device.
                  </p>
                </div>
                <Switch />
              </div>
              <div>
                {[
                  {
                    title: "Your call has been confirmed.",
                    description: "1 hour ago",
                  },
                  {
                    title: "You have a new message!",
                    description: "1 hour ago",
                  },
                  {
                    title: "Your subscription is expiring soon!",
                    description: "2 hours ago",
                  },
                ].map((notification, index) => (
                  <div
                    key={index}
                    className="mb-4 grid grid-cols-[25px_1fr] items-start pb-4 last:mb-0 last:pb-0"
                  >
                    <span className="flex h-2 w-2 translate-y-1 rounded-full bg-primary" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {notification.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {notification.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Content>
            <Card.Footer>
              <Button  className="w-full" label="Mark all as read">
                <Icon name="check" className="mr-2 h-4 w-4" /> Mark all as read
              </Button>
            </Card.Footer>
          </Card>
        </ComponentPreview>
      </ComponentSection>

      {/* Pricing Cards */}
      <ComponentSection title="Pricing Cards" description="Cards for displaying pricing">
        <ComponentPreview className="flex-wrap gap-4">
          <Card className="w-[250px]">
            <Card.Header>
              <Card.Title>Free</Card.Title>
              <Card.Description>For individuals</Card.Description>
            </Card.Header>
            <Card.Content>
              <div className="text-3xl font-bold">$0</div>
              <p className="text-sm text-muted-foreground">per month</p>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex items-center">
                  <Icon name="check" className="mr-2 h-4 w-4 text-primary" />
                  5 projects
                </li>
                <li className="flex items-center">
                  <Icon name="check" className="mr-2 h-4 w-4 text-primary" />
                  Basic analytics
                </li>
              </ul>
            </Card.Content>
            <Card.Footer>
              <Button variant="outline" className="w-full" label="Get Started">
                Get Started
              </Button>
            </Card.Footer>
          </Card>

          <Card className="w-[250px] border-primary">
            <Card.Header>
              <Card.Title>Pro</Card.Title>
              <Card.Description>For teams</Card.Description>
            </Card.Header>
            <Card.Content>
              <div className="text-3xl font-bold">$29</div>
              <p className="text-sm text-muted-foreground">per month</p>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex items-center">
                  <Icon name="check" className="mr-2 h-4 w-4 text-primary" />
                  Unlimited projects
                </li>
                <li className="flex items-center">
                  <Icon name="check" className="mr-2 h-4 w-4 text-primary" />
                  Advanced analytics
                </li>
                <li className="flex items-center">
                  <Icon name="check" className="mr-2 h-4 w-4 text-primary" />
                  Priority support
                </li>
              </ul>
            </Card.Content>
            <Card.Footer>
              <Button className="w-full" label="Subscribe">Subscribe</Button>
            </Card.Footer>
          </Card>
        </ComponentPreview>
      </ComponentSection>

      {/* Icon Cards */}
      <ComponentSection title="Icon Cards" description="Cards with prominent icons">
        <ComponentPreview className="flex-wrap gap-4">
          <Card className="w-[200px]">
            <Card.Header className="text-center">
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Icon name="credit-card" className="h-6 w-6 text-primary" />
              </div>
              <Card.Title className="text-lg">Payments</Card.Title>
            </Card.Header>
            <Card.Content className="text-center">
              <p className="text-sm text-muted-foreground">
                Manage your payment methods
              </p>
            </Card.Content>
          </Card>

          <Card className="w-[200px]">
            <Card.Header className="text-center">
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Icon name="settings" className="h-6 w-6 text-primary" />
              </div>
              <Card.Title className="text-lg">Settings</Card.Title>
            </Card.Header>
            <Card.Content className="text-center">
              <p className="text-sm text-muted-foreground">
                Configure your preferences
              </p>
            </Card.Content>
          </Card>
        </ComponentPreview>
      </ComponentSection>

      {/* Simple Cards */}
      <ComponentSection title="Simple Cards" description="Minimal card variations">
        <ComponentPreview className="flex-wrap gap-4">
          <Card className="w-[250px]">
            <Card.Header>
              <Card.Title>Simple Card</Card.Title>
            </Card.Header>
            <Card.Content>
              <p className="text-sm text-muted-foreground">
                A card with just a header and content.
              </p>
            </Card.Content>
          </Card>

          <Card className="w-[250px]">
            <Card.Content className="pt-6">
              <p className="text-sm">A card with only content, no header.</p>
            </Card.Content>
          </Card>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  )
}
